import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import sqfTrainingService from '../../services/sqfTrainingService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const STATUS_COLORS = {
  pending:     'warning',
  in_progress: 'info',
  passed:      'success',
  failed:      'danger',
  expired:     'secondary',
};

const STATUS_LABELS = {
  pending:     { en: 'Pending',     es: 'Pendiente'    },
  in_progress: { en: 'In Progress', es: 'En Progreso'  },
  passed:      { en: 'Passed',      es: 'Aprobado'     },
  failed:      { en: 'Failed',      es: 'Reprobado'    },
  expired:     { en: 'Expired',     es: 'Vencido'      },
};

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

export default function SqfTrainingAssignmentsPage() {
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const defaultTrainingId = searchParams.get('trainingId') || '';

  const [assignments, setAssignments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null);

  // Results modal
  const [resultsModal, setResultsModal]       = useState(null); // { assignment, attemptIdx }
  const [loadingResultsId, setLoadingResultsId] = useState(null);

  // Filters
  const [filterTraining, setFilterTraining] = useState(defaultTrainingId);
  const [filterStatus, setFilterStatus] = useState('');

  // Assign modal
  const [showModal, setShowModal] = useState(false);
  const [assignTrainingId, setAssignTrainingId] = useState(defaultTrainingId);
  const [assignEmployeeIds, setAssignEmployeeIds] = useState([]);
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  const loadAssignments = useCallback(async () => {
    const params = {};
    if (filterTraining) params.trainingId = filterTraining;
    if (filterStatus)   params.status     = filterStatus;
    try {
      const data = await sqfTrainingService.getAssignments(params);
      setAssignments(data || []);
    } catch {
      setError(t('Failed to load assignments.', 'Error al cargar las asignaciones.', lang));
    }
  }, [filterTraining, filterStatus, lang]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      sqfTrainingService.getTrainings(),
      loadAssignments(),
    ]).then(([ts]) => {
      setTrainings(ts || []);
    }).catch(() => setError(t('Failed to load data.', 'Error al cargar los datos.', lang))).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!loading) loadAssignments();
  }, [filterTraining, filterStatus]); // eslint-disable-line

  // Load employees only when modal opens
  const loadEmployees = useCallback(async () => {
    try {
      const data = await sqfTrainingService.getTrainingEmployees();
      setEmployees(data || []);
    } catch {
      setError(t('Failed to load employees.', 'Error al cargar los empleados.', lang));
    }
  }, [lang]);

  useEffect(() => {
    if (showModal && employees.length === 0) loadEmployees();
  }, [showModal, employees.length, loadEmployees]);

  const handleAssign = async () => {
    if (!assignTrainingId || !assignEmployeeIds.length) return;
    setAssigning(true);
    setAssignResult(null);
    try {
      const result = await sqfTrainingService.createAssignments({
        trainingId: assignTrainingId,
        employeeIds: assignEmployeeIds,
        dueDate: assignDueDate || null,
      });
      setAssignResult(result);
      setAssignEmployeeIds([]);
      setAssignDueDate('');
      await loadAssignments();
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to assign training.', 'Error al asignar la capacitación.', lang));
    } finally {
      setAssigning(false);
    }
  };

  const handleRevoke = (assignment) => {
    const name = `${assignment.Employee?.firstName} ${assignment.Employee?.lastName}`;
    setConfirmModal({
      title: t('Revoke Assignment', 'Revocar Asignación', lang),
      message: t(
        `Remove the pending training assignment for ${name}? This cannot be undone.`,
        `¿Eliminar la asignación de capacitación pendiente de ${name}? Esta acción no se puede deshacer.`,
        lang
      ),
      confirmLabel: t('Revoke', 'Revocar', lang),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await sqfTrainingService.revokeAssignment(assignment.id);
          setAssignments(as => as.filter(a => a.id !== assignment.id));
        } catch (err) {
          setError(err?.response?.data?.message || t('Failed to revoke assignment.', 'Error al revocar la asignación.', lang));
        }
      },
    });
  };

  const handleViewResults = async (a) => {
    setLoadingResultsId(a.id);
    try {
      const full = await sqfTrainingService.getAssignment(a.id);
      setResultsModal({ assignment: full, attemptIdx: 0 });
    } catch {
      setError(t('Failed to load results.', 'Error al cargar los resultados.', lang));
    } finally {
      setLoadingResultsId(null);
    }
  };

  const toggleEmployee = (id) => {
    setAssignEmployeeIds(ids =>
      ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]
    );
  };

  const selectedTraining = trainings.find(tr => String(tr.id) === String(assignTrainingId));

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <Link to="/sqf/training" className="text-muted" style={{ fontSize: 12 }}>
              ← {t('Training Library', 'Biblioteca de Capacitaciones', lang)}
            </Link>
            <h4 className="mb-0 mt-1">{t('Training Assignments', 'Asignaciones de Capacitación', lang)}</h4>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowModal(true); setAssignResult(null); }}>
            + {t('Assign Training', 'Asignar Capacitación', lang)}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger py-2">
            {error}
            <button className="btn-close float-end btn-sm" onClick={() => setError('')} />
          </div>
        )}

        {/* Filters */}
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 240 }}
            value={filterTraining}
            onChange={e => setFilterTraining(e.target.value)}
          >
            <option value="">{t('All trainings', 'Todas las capacitaciones', lang)}</option>
            {trainings.map(tr => (
              <option key={tr.id} value={String(tr.id)}>{tr.title}</option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 160 }}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">{t('All statuses', 'Todos los estados', lang)}</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{t(v.en, v.es, lang)}</option>
            ))}
          </select>
          {(filterTraining || filterStatus) && (
            <button
              className="btn btn-sm btn-link text-muted p-0"
              onClick={() => { setFilterTraining(''); setFilterStatus(''); }}
            >
              {t('Clear filters', 'Limpiar filtros', lang)}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5 text-muted">
            <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
          </div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <div style={{ fontSize: 40 }}>📋</div>
            <p className="mt-2">{t('No assignments found.', 'No se encontraron asignaciones.', lang)}</p>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
              + {t('Assign Training', 'Asignar Capacitación', lang)}
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover">
              <thead className="table-light">
                <tr>
                  <th>{t('Employee', 'Empleado', lang)}</th>
                  <th>{t('Training', 'Capacitación', lang)}</th>
                  <th style={{ width: 90 }}>{t('Version', 'Versión', lang)}</th>
                  <th style={{ width: 110 }}>{t('Status', 'Estado', lang)}</th>
                  <th style={{ width: 90 }}>{t('Score', 'Puntaje', lang)}</th>
                  <th style={{ width: 100 }}>{t('Assigned', 'Asignado', lang)}</th>
                  <th style={{ width: 100 }}>{t('Due', 'Vencimiento', lang)}</th>
                  <th style={{ width: 110 }}>{t('Completed', 'Completado', lang)}</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map(a => {
                  const statusLabel = STATUS_LABELS[a.status];
                  return (
                    <tr key={a.id}>
                      <td>
                        <span className="fw-semibold">
                          {a.Employee?.firstName} {a.Employee?.lastName}
                        </span>
                      </td>
                      <td>
                        <Link to={`/sqf/training/${a.trainingId}/edit`} className="text-decoration-none">
                          {a.Training?.title}
                        </Link>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {a.Version ? `v${a.Version.versionNumber}` : '—'}
                      </td>
                      <td>
                        <span className={`badge bg-${STATUS_COLORS[a.status] || 'secondary'}`}>
                          {statusLabel ? t(statusLabel.en, statusLabel.es, lang) : a.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {a.bestScore != null
                          ? <span className={a.bestScore >= (a.Version?.passingScore ?? 80) ? 'text-success fw-semibold' : 'text-danger'}>
                              {a.bestScore}%
                            </span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmt(a.assignedAt)}</td>
                      <td style={{ fontSize: 12 }}>
                        {a.dueDate
                          ? <span className={a.status === 'expired' ? 'text-danger' : ''}>{fmt(a.dueDate)}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmt(a.completedAt)}</td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          {a.bestScore != null && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              style={{ fontSize: 11 }}
                              onClick={() => handleViewResults(a)}
                              disabled={loadingResultsId === a.id}
                            >
                              {loadingResultsId === a.id
                                ? <span className="spinner-border spinner-border-sm" />
                                : t('Results', 'Resultados', lang)}
                            </button>
                          )}
                          {a.status === 'pending' && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              style={{ fontSize: 11 }}
                              onClick={() => handleRevoke(a)}
                            >
                              {t('Revoke', 'Revocar', lang)}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              {assignments.length} {t('assignment', 'asignación', lang)}{assignments.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('Assign Training', 'Asignar Capacitación', lang)}</h5>
                <button className="btn-close" onClick={() => { setShowModal(false); setAssignResult(null); }} />
              </div>
              <div className="modal-body">
                {assignResult && (
                  <div className="alert alert-success py-2 mb-3">
                    {t(
                      `Assigned to ${assignResult.created} employee${assignResult.created !== 1 ? 's' : ''}.${assignResult.skipped > 0 ? ` ${assignResult.skipped} already had an active assignment and were skipped.` : ''}`,
                      `Asignado a ${assignResult.created} empleado${assignResult.created !== 1 ? 's' : ''}.${assignResult.skipped > 0 ? ` ${assignResult.skipped} ya tenían una asignación activa y fueron omitidos.` : ''}`,
                      lang
                    )}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t('Training', 'Capacitación', lang)} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={assignTrainingId}
                    onChange={e => setAssignTrainingId(e.target.value)}
                  >
                    <option value="">{t('— Select a training —', '— Selecciona una capacitación —', lang)}</option>
                    {trainings.filter(tr => tr.activeVersionId).map(tr => (
                      <option key={tr.id} value={String(tr.id)}>{tr.title}</option>
                    ))}
                  </select>
                  {trainings.filter(tr => !tr.activeVersionId).length > 0 && (
                    <div className="form-text text-muted">
                      {t('Trainings without a published version are not shown.', 'Las capacitaciones sin versión publicada no se muestran.', lang)}
                    </div>
                  )}
                  {assignTrainingId && !selectedTraining?.activeVersionId && (
                    <div className="alert alert-warning py-2 mt-2">
                      {t('This training has no published version and cannot be assigned.', 'Esta capacitación no tiene versión publicada y no puede asignarse.', lang)}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t('Due Date (optional)', 'Fecha de Vencimiento (opcional)', lang)}
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={assignDueDate}
                    onChange={e => setAssignDueDate(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t('Employees', 'Empleados', lang)} <span className="text-danger">*</span>
                    {assignEmployeeIds.length > 0 && (
                      <span className="badge bg-primary ms-2">{assignEmployeeIds.length} {t('selected', 'seleccionados', lang)}</span>
                    )}
                  </label>
                  {employees.length === 0 ? (
                    <div className="text-center py-3 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" />{t('Loading employees…', 'Cargando empleados…', lang)}
                    </div>
                  ) : (
                    <div className="border rounded p-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
                      <div className="d-flex gap-2 mb-2">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          style={{ fontSize: 11 }}
                          onClick={() => setAssignEmployeeIds(employees.map(e => e.id))}
                        >
                          {t('Select all', 'Seleccionar todos', lang)}
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          style={{ fontSize: 11 }}
                          onClick={() => setAssignEmployeeIds([])}
                        >
                          {t('Clear', 'Limpiar', lang)}
                        </button>
                      </div>
                      {employees.map(emp => (
                        <div
                          key={emp.id}
                          className="form-check"
                          onClick={() => toggleEmployee(emp.id)}
                          style={{ cursor: 'pointer', padding: '3px 0 3px 28px' }}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            readOnly
                            checked={assignEmployeeIds.includes(emp.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label" style={{ cursor: 'pointer', fontSize: 14 }}>
                            {emp.firstName} {emp.lastName}
                            {emp.badgeNumber && (
                              <span className="text-muted ms-2" style={{ fontSize: 12 }}>#{emp.badgeNumber}</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => { setShowModal(false); setAssignResult(null); }}
                >
                  {assignResult ? t('Close', 'Cerrar', lang) : t('Cancel', 'Cancelar', lang)}
                </button>
                {!assignResult && (
                  <button
                    className="btn btn-primary"
                    onClick={handleAssign}
                    disabled={assigning || !assignTrainingId || !assignEmployeeIds.length}
                  >
                    {assigning
                      ? <><span className="spinner-border spinner-border-sm me-2" />{t('Assigning…', 'Asignando…', lang)}</>
                      : `${t('Assign to', 'Asignar a', lang)} ${assignEmployeeIds.length || '…'} ${t('employee', 'empleado', lang)}${assignEmployeeIds.length !== 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {resultsModal && (() => {
        const { assignment: ra, attemptIdx } = resultsModal;
        const attempt    = (ra.Attempts || [])[attemptIdx];
        const questions  = ra.Version?.questionsJson || [];
        const graded     = attempt?.responsesJson || [];

        return (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title mb-0">
                      {ra.Employee?.firstName} {ra.Employee?.lastName} — {ra.Training?.title}
                    </h5>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      v{ra.Version?.versionNumber} · {t('Passing score', 'Puntaje aprobatorio', lang)}: {ra.Version?.passingScore ?? 80}%
                    </div>
                  </div>
                  <button className="btn-close ms-auto" onClick={() => setResultsModal(null)} />
                </div>
                <div className="modal-body p-0">
                  <div className="d-flex" style={{ minHeight: 400 }}>

                    {/* Attempts sidebar */}
                    <div style={{ width: 190, flexShrink: 0, borderRight: '1px solid #e9ecef', background: '#f8f9fa', padding: '12px 8px' }}>
                      <div className="fw-semibold mb-2" style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d', padding: '0 4px' }}>
                        {t('Attempts', 'Intentos', lang)}
                      </div>
                      {(ra.Attempts || []).length === 0 ? (
                        <div className="text-muted" style={{ fontSize: 12, padding: '4px' }}>
                          {t('No attempts.', 'Sin intentos.', lang)}
                        </div>
                      ) : (ra.Attempts || []).map((att, i) => (
                        <button
                          key={att.id}
                          onClick={() => setResultsModal(prev => ({ ...prev, attemptIdx: i }))}
                          className="w-100 text-start border-0 rounded p-2 mb-1"
                          style={{
                            background: i === attemptIdx ? '#fff' : 'transparent',
                            boxShadow: i === attemptIdx ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#6c757d' }}>
                            #{ra.Attempts.length - i} · {new Date(att.submittedAt || att.startedAt).toLocaleDateString()}
                            {att.durationSeconds && (
                              <span className="ms-1">· {Math.round(att.durationSeconds / 60)}m</span>
                            )}
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: att.passed ? '#198754' : '#dc3545' }}>
                            {att.score}%
                          </div>
                          <span className={`badge bg-${att.passed ? 'success' : 'danger'}`} style={{ fontSize: 10 }}>
                            {att.passed ? t('Passed', 'Aprobado', lang) : t('Failed', 'Reprobado', lang)}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Question review */}
                    <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
                      {!attempt ? (
                        <div className="text-center text-muted py-4">
                          {t('No attempts to review.', 'Sin intentos para revisar.', lang)}
                        </div>
                      ) : questions.map((q, idx) => {
                        const g = graded.find(r => r.questionId === q.id);
                        return (
                          <div key={q.id} className="mb-4 pb-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <span className="fw-semibold" style={{ fontSize: 14 }}>
                                {idx + 1}. {q.text}
                              </span>
                              <span style={{ fontSize: 18, flexShrink: 0, marginLeft: 8 }}>
                                {g?.correct ? '✅' : '❌'}
                              </span>
                            </div>
                            <div className="d-flex flex-column gap-1">
                              {(q.options || []).map(opt => {
                                const wasSelected  = (g?.selectedIds || []).includes(opt.id);
                                const isCorrectOpt = (q.correctIds   || []).includes(opt.id);
                                let bg = 'transparent', color = '#6c757d', icon = '  ';
                                if      (wasSelected && isCorrectOpt)  { bg = '#e9f7ef'; color = '#198754'; icon = '✓ '; }
                                else if (wasSelected && !isCorrectOpt) { bg = '#fdecea'; color = '#dc3545'; icon = '✗ '; }
                                else if (!wasSelected && isCorrectOpt) { bg = '#fff8e1'; color = '#856404'; icon = '→ '; }
                                return (
                                  <div key={opt.id} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 13, background: bg, color, fontWeight: wasSelected || isCorrectOpt ? 600 : 400 }}>
                                    {icon}{opt.text}
                                    {!wasSelected && isCorrectOpt && (
                                      <span className="ms-2 badge bg-warning text-dark" style={{ fontSize: 10 }}>
                                        {t('correct answer', 'respuesta correcta', lang)}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {q.explanation && (
                              <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: 12, color: '#495057' }}>
                                💡 {q.explanation}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content shadow">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-semibold">{confirmModal.title}</h5>
                <button className="btn-close" onClick={() => setConfirmModal(null)} />
              </div>
              <div className="modal-body pt-2">
                <p className="mb-0 text-muted" style={{ fontSize: 14 }}>{confirmModal.message}</p>
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button className="btn btn-outline-secondary" onClick={() => setConfirmModal(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className={`btn btn-${confirmModal.variant || 'primary'}`}
                  onClick={async () => {
                    const fn = confirmModal.onConfirm;
                    setConfirmModal(null);
                    await fn?.();
                  }}
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
